import React, { type FC, useEffect, useMemo, useState } from "react";
import {
  type CountryData,
  type CountryIso2,
  defaultCountries,
  parseCountry,
  PhoneInput
} from "react-international-phone";
import "react-international-phone/style.css";
import "./styles.scss";
import { getCountriesByLanguage } from "../../utils/i18n/getCountriesByLang";
import { FormikTouched } from "formik/dist/types";
import BusinessUser from "@tootsweet/model/entities/BusinessUser";

interface KC_PhoneInputProps {
  id: string;
  htmlFor: string;
  value: string;
  setFieldValue: (field: string, value: any) => void;
  placeholder?: string;
  required?: boolean;
  defaultCountry?: CountryIso2;
  disabled?: boolean;
  setFieldTouched?: (field: string, touched: boolean) => void;
  touched?: FormikTouched<BusinessUser>;
  errors?: any;
  onCountryChange?: (countryIso2: string) => void;
}

const KC_PhoneInput: FC<KC_PhoneInputProps> = (props) => {
  const [phone, setPhone] = useState(props.value || "");

  const { NAMES_BY_ISO_CODE } = useMemo(() => {
    return getCountriesByLanguage();
  }, []);

  const COUNTRIES = useMemo<CountryData[]>(() => {
    return defaultCountries.map((country) => {
      const parsed = parseCountry(country);
      const countryCodeUpper = parsed.iso2.toUpperCase();
      const localizedName = NAMES_BY_ISO_CODE[countryCodeUpper];

      if (localizedName) {
        return [
          localizedName,
          parsed.iso2,
          parsed.dialCode,
          parsed.format,
          parsed.priority,
          parsed.areaCodes
        ] as CountryData;
      }

      return country;
    });
  }, [NAMES_BY_ISO_CODE]);

  useEffect(() => {
    if (props.value !== phone) {
      setPhone(props.value || "");
    }
  }, [props.value]);

  const handlePhoneChange = (
    phoneValue: string,
    meta: { country: { iso2: string }; inputValue: string }
  ) => {
    setPhone(phoneValue);
    props.setFieldValue(props.htmlFor, phoneValue);
    props.onCountryChange?.(meta.country.iso2);
  };

  const handleBlur = () => {
    props.setFieldTouched?.(props.htmlFor, true);
  };

  const error = useMemo(() => {
    const touched = getNestedValue(props.touched, props.htmlFor);
    const res = getNestedValue(props.errors, props.htmlFor);
    return res ?? null;
  }, [props.touched, props.htmlFor, props.errors]);

  return (
    <div className="kc-phone-input-wrapper">
      <PhoneInput
        defaultCountry={
          props.defaultCountry === undefined ? "FR" : props.defaultCountry
        }
        className="kc_input kc-phone-input"
        inputClassName="w-100 react-international-phone-input-container-override"
        value={phone}
        onChange={handlePhoneChange}
        placeholder={props.placeholder}
        disabled={props.disabled}
        inputProps={{
          id: props.id,
          required: props.required,
          name: props.htmlFor,
          onBlur: handleBlur
        }}
        countries={COUNTRIES}
      />
      {!!error && <div className="error mt-2">{error}</div>}
    </div>
  );
};

export default KC_PhoneInput;

const getNestedValue = (obj: any, path: string) => {
  if (!obj) {
    return undefined;
  }

  const keys = path.split(".");
  let value = obj;

  for (const key of keys) {
    value = value?.[key];

    if (value === undefined) {
      return undefined;
    }
  }

  return value;
};
